import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  IconButton,
  Button,
  LinearProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { audioActions } from "store/audioSlice";
import AudioPlayer from "components/audioPlayer/AudioPlayer";
import {
  useCollectionData,
  useDocumentData,
  useDocumentDataOnce,
} from "react-firebase-hooks/firestore";
import { db, functions } from "firebaseApp";
import { collection, doc, getDoc, setDoc, orderBy, query } from "firebase/firestore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { httpsCallable } from "firebase/functions";
import AlertMessage from "components/react-lib/generic-ui/AlertMessage";
import PlayPauseButton from "../PlayPauseButton";
import { useAuth } from "context/react-lib/firebase-auth/AuthProvider";
import { popupActions } from "store/react-lib/generic-ui/popup";

const AudioTextFilePlayer = ({ audioTextFileId }) => {
  const dispatch = useDispatch();

  const auth = useAuth();
  const uid = auth.currentUser?.uid;

  const { isPlaying } = useSelector((state) => state.audio);

  const [currentPage, setCurrentPage] = useState(null);
  const [currentSentence, setCurrentSentence] = useState(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [regeneratingAudio, setRegeneratingAudio] = useState(false);

  console.log("CURRENT", currentPage, currentSentence);

  const audioTextFileRef = audioTextFileId
    ? doc(db, "audioTextFiles", audioTextFileId)
    : null;

  const [audioTextFile, fileLoading, fileError] = useDocumentData(audioTextFileRef);

  if (fileError) console.log(fileError);

  const loadTrackerData = async () => {
    try {  
      const trackerRef = doc(db, "audioPlayer", uid, "audioTextFiles", audioTextFileId);
      const trackerSnap = await getDoc(trackerRef);
      const trackerData = trackerSnap.data();
      console.log("TRACKER", trackerData);
      setCurrentPage(trackerData.currentPage || 0);
      setCurrentSentence(trackerData.currentSentence || 0);
    } catch(error) {
      console.log(error);
      dispatch(popupActions.showError("Couldn't load audio tracker data"));
      setCurrentPage(0);
      setCurrentSentence(0);
    }
  }

  useEffect(() => {
    if(uid && audioTextFileId) {
      loadTrackerData();
    }
  }, [uid, audioTextFileId]);

  useEffect(() => {
    if (audioTextFileId && uid && currentSentence !== null && currentPage !== null) {
      console.log("UPDATE TRACKER", currentSentence, currentPage);
      const trackerRef = doc(db, "audioPlayer", uid, "audioTextFiles", audioTextFileId);
      setDoc(trackerRef, { currentPage, currentSentence }, { merge: true });
    }
  }, [currentPage, currentSentence, audioTextFileId, uid]);

  const pageRef = audioTextFileId
    ? doc(db, "audioTextFiles", audioTextFileId, "pages", `page-${currentPage}`)
    : null;

  const [pageData, pageLoading, pageError] = useDocumentData(pageRef);

  if (pageError) console.log(pageError);

  const partsRef = audioTextFileId
    ? query(
        collection(
          db,
          "audioTextFiles",
          audioTextFileId,
          "pages",
          `page-${currentPage}`,
          "parts"
        ),
        orderBy("index", "asc")
      )
    : null;

  const [partsData, loadingParts, partsError] = useCollectionData(partsRef);

  if (partsError) console.log(partsError);

  const hasMorePages =
    audioTextFile && currentPage + 1 < audioTextFile.totalPages;

  const hasMoreSentences =
    pageData && pageData.numParts && currentSentence + 1 < pageData.numParts;

  const generatePage = async (page) => {
    console.log("GENERATE PAGE");
    try {
      await httpsCallable(
        functions,
        "generatePageAudio"
      )({
        audioTextFileId,
        page,
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (audioTextFileId && !pageLoading && !pageData && currentPage > 0) {
      generatePage(currentPage);
    }
  }, [audioTextFileId, currentPage, pageData, pageLoading]);

  const generateNextPage = async () => {
    const nextPageRef = doc(
      db,
      "audioTextFiles",
      audioTextFileId,
      "pages",
      `page-${currentPage + 1}`
    );
    const nextPageSnap = await getDoc(nextPageRef);
    if (!nextPageSnap.exists()) {
      await generatePage(currentPage + 1);
    }
  };

  useEffect(() => {
    if (audioTextFile && currentPage + 1 < audioTextFile.totalPages) {
      generateNextPage();
    }
  }, [currentPage, audioTextFile]);

  const handleAudioEnd = useCallback(() => {
    if (hasMoreSentences) {
      setCurrentSentence((prev) => prev + 1);
    } else if (hasMorePages) {
      setCurrentPage((prev) => prev + 1);
      setCurrentSentence(0);
    } else {
      console.log("End of document reached");
      dispatch(audioActions.pause());
    }
  }, [hasMoreSentences, hasMorePages, dispatch]);

  useEffect(() => {
    const audioElement = document.querySelector("audio");
    if (audioElement) {
      audioElement.addEventListener("ended", handleAudioEnd);
      return () => audioElement.removeEventListener("ended", handleAudioEnd);
    }
  }, [handleAudioEnd]);

  useEffect(() => {
    if (!pageData || pageLoading) {
      setIsWaiting(true);
      return;
    }

    const parts = partsData || [];

    if (parts.length > currentSentence && parts[currentSentence].audioUrl) {
      setIsWaiting(false);
      dispatch(audioActions.setAudioUrl(parts[currentSentence].audioUrl));
      dispatch(audioActions.play());
    } else {
      setIsWaiting(true);
      console.log("Waiting for audio to be processed...");
    }
  }, [
    partsData,
    pageData,
    currentPage,
    currentSentence,
    pageLoading,
    dispatch,
  ]);

  if (!audioTextFileId) return null;

  if (fileLoading || !audioTextFile || currentPage === null || currentSentence === null) {
    return <CircularProgress />;
  }

  if (fileError) {
    console.log(fileError);
    return (
      <Typography color="error">
        Error loading file.
      </Typography>
    );
  }

  const handleGoToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
      setCurrentSentence(0);
    }
  };

  const handleGoToPreviousSentence = () => {
    if (currentSentence > 0) {
      setCurrentSentence((prev) => prev - 1);
    } else {
      dispatch(audioActions.resetAudioPosition());
    }
  };

  const handleGoToNextPage = () => {
    if (currentPage + 1 < audioTextFile.totalPages) {
      setCurrentPage((prev) => prev + 1);
      setCurrentSentence(0);
    }
  };

  const handleGoToNextSentence = () => {
    if (hasMoreSentences) {
      setCurrentSentence((prev) => prev + 1);
    }
  };

  const handleRegenerateAudio = () => {
    setRegeneratingAudio(true);
    httpsCallable(
      functions,
      "regeneratePageAudio"
    )({
      audioTextFileId,
      pageId: `page-${currentPage}`,
    }).catch((error) => console.log(error));
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {audioTextFile.fileName}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography>
          Page {currentPage + 1} of {audioTextFile.totalPages}
        </Typography>
        {/* {pageData && (
          <Typography>
            Part {currentSentence + 1} of {pageData.numParts}
          </Typography>
        )} */}
      </Box>

      <LinearProgress color="secondary" sx={{ opacity: isWaiting ? 0.5 : 0 }} />

      <Stack direction="row" justifyContent="space-between" spacing={4} my={2}>
        <Stack direction="row">
          <IconButton
            disabled={currentPage === 0}
            onClick={handleGoToPreviousPage}
          >
            <SkipPreviousIcon />
          </IconButton>
          <IconButton onClick={handleGoToPreviousSentence}>
            <NavigateBeforeIcon />
          </IconButton>
        </Stack>
        <PlayPauseButton />
        <Stack direction="row">
          <IconButton
            onClick={handleGoToNextSentence}
            disabled={!hasMoreSentences}
          >
            <NavigateNextIcon />
          </IconButton>
          <IconButton onClick={handleGoToNextPage} disabled={!hasMorePages}>
            <SkipNextIcon />
          </IconButton>
        </Stack>
      </Stack>

      {pageData && pageData.error && (
        <Stack pt={0.5} spacing={1}>
          <AlertMessage severity="error">
            There was an error generating audio.
          </AlertMessage>
          <Button
            variant="outlined"
            fullWidth
            disabled={regeneratingAudio}
            onClick={handleRegenerateAudio}
          >
            Regenerate page audio
          </Button>
        </Stack>
      )}
      <AudioPlayer />
    </Paper>
  );
};

export default AudioTextFilePlayer;